import { documentPayloadSchema } from './models/document.mjs';
import { workbookPayloadSchema } from './models/workbook.mjs';
import {
  reportPayloadSchema,
  deckPayloadSchema,
  tablePayloadSchema,
  chartPayloadSchema,
  dashboardPayloadSchema,
  kpiPayloadSchema,
  markdownPayloadSchema,
} from './models/native.mjs';
import { readOnlyReactPayloadSchema } from './models/react.mjs';

function descriptor(
  id,
  payloadSchema,
  formats,
  { refresh = false, present = false, code = false, authoring = true } = {},
) {
  return Object.freeze({
    id,
    schemaVersion: 1,
    labelKey: `artifacts.kind.${id}`,
    payloadSchema,
    capabilities: Object.freeze({
      authoring,
      inspector: id !== 'markdown',
      refresh,
      present,
      readOnlyReact: code,
    }),
    exportFormats: Object.freeze(formats),
    enabledIn: Object.freeze(['pr1', 'pr2']),
  });
}
export const artifactTypes = Object.freeze([
  descriptor('report', reportPayloadSchema, ['pdf', 'docx'], { refresh: true }),
  descriptor('deck', deckPayloadSchema, ['pptx', 'pdf'], { present: true }),
  descriptor('document', documentPayloadSchema, ['docx', 'pdf']),
  descriptor('spreadsheet', workbookPayloadSchema, ['xlsx', 'csv', 'pdf']),
  descriptor('table', tablePayloadSchema, ['xlsx', 'csv', 'pdf']),
  descriptor('chart', chartPayloadSchema, ['pdf']),
  descriptor('dashboard', dashboardPayloadSchema, ['pdf']),
  // Retained display/export schemas for existing Results. New authoring uses
  // the frozen canonical envelope kinds; no implicit conversion is performed.
  descriptor('kpi', kpiPayloadSchema, ['pdf'], { authoring: false }),
  descriptor('markdown', markdownPayloadSchema, ['docx', 'pdf'], { authoring: false }),
  descriptor('page', readOnlyReactPayloadSchema, ['pdf'], { refresh: true, code: true }),
  descriptor('site', readOnlyReactPayloadSchema, ['pdf'], { code: true }),
]);
const byId = new Map(artifactTypes.map((type) => [type.id, type]));
export function findArtifactType(id) {
  return byId.get(id) ?? null;
}
export function getArtifactType(id, schemaVersion = 1) {
  const type = findArtifactType(id);
  if (!type || type.schemaVersion !== schemaVersion)
    throw new TypeError('unsupported_artifact_type');
  return type;
}
export function listArtifactTypes({ release = 'pr1' } = {}) {
  return artifactTypes.filter((type) => type.enabledIn.includes(release));
}
export function parseArtifactPayload(kind, payload, schemaVersion = 1) {
  return getArtifactType(kind, schemaVersion).payloadSchema.parse(payload);
}
// A PR2 consumer supplies its App descriptor through its own registration;
// importing this PR1 foundation never enables App authoring or App routes.
export function createArtifactRegistry(extensions = [], { release = 'pr1' } = {}) {
  const types = new Map(listArtifactTypes({ release }).map((type) => [type.id, type]));
  for (const type of extensions) {
    if (
      release !== 'pr2' ||
      type.id !== 'app' ||
      types.has(type.id) ||
      !type.payloadSchema?.safeParse
    )
      throw new TypeError('invalid_artifact_extension');
    types.set(type.id, Object.freeze({ ...type, enabledIn: Object.freeze(['pr2']) }));
  }
  return Object.freeze({
    list: () => [...types.values()],
    get: (id) => {
      const type = types.get(id);
      if (!type) throw new TypeError('unsupported_artifact_type');
      return type;
    },
  });
}
